/// <reference lib="webworker" />

/**
 * STP-OCCT Worker - STEP file parsing in background thread.
 * Loads occt-import-js WASM and runs ReadStepFile() off main thread.
 * Streams mesh chunks (5 at a time) back to main thread for progressive display.
 *
 * Message protocol:
 *   Main → Worker: { type: 'parseStep', buffer: ArrayBuffer, chunkSize?: number, linearDeflection?: number, angularDeflection?: number }
 *   Worker → Main: { type: 'meshChunk', meshes: MeshData[], progress: number }
 *   Worker → Main: { type: 'complete', totalMeshes: number, elapsed: number }
 *   Worker → Main: { type: 'error', error: string }
 */

declare const self: DedicatedWorkerGlobalScope;

interface OcctMeshData {
  index: { array: Uint32Array };
  attributes: {
    position: { array: Float32Array };
    normal?: { array: Float32Array };
  };
  name?: string;
  color?: number[];
}

interface OcctResult {
  meshes: OcctMeshData[];
  root?: any;
  compounds?: any;
}

interface MeshChunk {
  position: ArrayBuffer;
  normal: ArrayBuffer | null;
  index: ArrayBuffer;
  name: string;
  color: number[];
  vertexCount: number;
  indexCount: number;
}

let occtWasm: any = null;

/**
 * Initialize occt-import-js WASM module.
 */
async function initOcctWasm(): Promise<any> {
  if (occtWasm) return occtWasm;

  try {
    // occt-import-js is loaded as external dependency
    // In worker context, importScripts is available
    (self as any).importScripts('/occt/occt-import-js.js');

    if (typeof (self as any).occtimportjs === 'function') {
      occtWasm = await (self as any).occtimportjs();
      return occtWasm;
    }

    throw new Error('occtimportjs function not found after importScripts');
  } catch (e: any) {
    throw new Error(`OCCT WASM init failed: ${e.message}`);
  }
}

/**
 * Parse STEP file and stream meshes back in chunks.
 */
async function parseStepFile(
  buffer: ArrayBuffer,
  chunkSize: number,
  customLinearDeflection?: number,
  customAngularDeflection?: number
): Promise<void> {
  const startTime = performance.now();

  // Initialize OCCT
  const occt = await initOcctWasm();

  // Read STEP file - use provided quality params or defaults
  const fileBuffer = new Uint8Array(buffer);
  const linDefl = customLinearDeflection ?? 0.01;
  const angDefl = customAngularDeflection ?? 0.1;

  console.log(`[STP-OCCT Worker] Tessellation kalitesi: linearDeflection=${linDefl}, angularDeflection=${angDefl}`);

  const result: OcctResult = occt.ReadStepFile(fileBuffer, {
    linearDeflection: linDefl,
    angularDeflection: angDefl,
  });

  if (!result || !result.meshes || result.meshes.length === 0) {
    self.postMessage({ type: 'error', error: 'No meshes found in STEP file' });
    return;
  }

  const totalMeshes = result.meshes.length;

  // Send root/compounds metadata first
  self.postMessage({
    type: 'metadata',
    totalMeshes,
    root: result.root || null,
    compounds: result.compounds || null
  });

  // Stream meshes in chunks
  for (let i = 0; i < totalMeshes; i += chunkSize) {
    const end = Math.min(i + chunkSize, totalMeshes);
    const chunk: MeshChunk[] = [];
    const transfers: ArrayBuffer[] = [];

    for (let j = i; j < end; j++) {
      const mesh = result.meshes[j];
      if (!mesh?.attributes?.position?.array || !mesh?.index?.array) continue;

      const posArray = mesh.attributes.position.array;
      const idxArray = mesh.index.array;
      const normArray = mesh.attributes.normal?.array || null;

      // Copy to new buffers for transfer
      const posBuf = posArray.buffer.slice(
        posArray.byteOffset,
        posArray.byteOffset + posArray.byteLength
      );
      const idxBuf = idxArray.buffer.slice(
        idxArray.byteOffset,
        idxArray.byteOffset + idxArray.byteLength
      );
      let normBuf: ArrayBuffer | null = null;
      if (normArray) {
        normBuf = normArray.buffer.slice(
          normArray.byteOffset,
          normArray.byteOffset + normArray.byteLength
        );
      }

      chunk.push({
        position: posBuf,
        normal: normBuf,
        index: idxBuf,
        name: mesh.name || `Part_${j}`,
        color: mesh.color || [0.7, 0.7, 0.7],
        vertexCount: posArray.length / 3,
        indexCount: idxArray.length
      });

      transfers.push(posBuf, idxBuf);
      if (normBuf) transfers.push(normBuf);
    }

    if (chunk.length > 0) {
      const progress = Math.min(1, end / totalMeshes);
      self.postMessage(
        { type: 'meshChunk', meshes: chunk, progress, chunkIndex: Math.floor(i / chunkSize) },
        transfers
      );
    }

    // Yield to allow message queue processing between chunks
    if (end < totalMeshes) {
      await new Promise(resolve => setTimeout(resolve, 0));
    }
  }

  const elapsed = performance.now() - startTime;
  self.postMessage({
    type: 'complete',
    totalMeshes,
    elapsed: Math.round(elapsed)
  });
}

// Message handler
self.onmessage = async (event: MessageEvent) => {
  const { type, buffer, chunkSize, linearDeflection, angularDeflection } = event.data;

  switch (type) {
    case 'parseStep':
      try {
        await parseStepFile(buffer, chunkSize || 5, linearDeflection, angularDeflection);
      } catch (e: any) {
        self.postMessage({ type: 'error', error: e.message || String(e) });
      }
      break;

    case 'ping':
      self.postMessage({ type: 'pong' });
      break;

    case 'terminate':
      self.close();
      break;

    default:
      self.postMessage({ type: 'error', error: `Unknown command: ${type}` });
  }
};

console.log('[STP-OCCT Worker] Ready');
